import { ChevronRightIcon, KitIcon } from 'components'
import { upperCase } from 'lodash-es'
import { useContext, useState } from 'react'
import { ISelectedTraitLayout, TraitValue } from 'types'
import { addLayoutTag } from '../../../utils/helpers'
import { parsePadding } from '../../../utils/layoutUtils'
import { useResultTraitGroupsWithSelections } from '../../../utils/resultUtils'
import { useScale } from '../../../utils/scaleUtils'
import { SessionContext } from '../../providers'
import { SButton, SDialog, SGrid, SIconCircle, SMarkdown, SText, SView } from '../../scaled'

export default function SelectedTraitLayout({
  groupTitle,
  learnMore,
  style,
  title,
  traitGroupKey,
}: Omit<ISelectedTraitLayout, 'type'>) {
  const { getLanguageValue } = useContext(SessionContext)
  const groups = useResultTraitGroupsWithSelections()
  const group = groups?.find(r => r.step.key === traitGroupKey)
  if (!group) return null

  const { selected, icon, isMulti, values } = group
  let titleString = getLanguageValue(groupTitle || group.title)
  if (!groupTitle)
    titleString = isMulti ? `MY ${upperCase(titleString)}S ARE` : `MY ${upperCase(titleString)} IS`
  const items = values.map(v => ({
    ...v,
    isSelected: selected.some(s =>
      s.traitId ? s.traitId === v.id : s.title === getLanguageValue(v.title)
    ),
  }))
  const padding = parsePadding({ style, title })

  if (isMulti) {
    return (
      <SView {...padding} gap={5} {...addLayoutTag('selected-trait')}>
        <SText fontSize={12} fontWeight="bold">
          {titleString}
        </SText>
        <SGrid columns={2} gap={5}>
          {items.map(({ title, isSelected }, i) => (
            <SText color={isSelected ? 'white' : '#C5C5C588'} key={i} fontSize={13}>
              • {getLanguageValue(title)}
            </SText>
          ))}
        </SGrid>
      </SView>
    )
  } else {
    const firstSelected = items.find(s => s.isSelected)
    if (!firstSelected) return null

    const valueString = getLanguageValue(firstSelected.title)
    const _icon = firstSelected.icon || icon || 'stack'

    return (
      <SView
        {...padding}
        direction="row"
        align="center"
        gap={10}
        justify="space-between"
        {...addLayoutTag('selected-trait')}
      >
        <SView direction="row" gap={10}>
          <SIconCircle Icon={props => <KitIcon {...props} type={_icon} />} />

          <SView gap={2} style={{ flexShrink: 1 }}>
            <SText fontSize={13}>{titleString}</SText>
            <SText fontWeight="bold" fontSize={20} lineClamp={1}>
              {valueString}
            </SText>
          </SView>
        </SView>

        {learnMore && <LearnMore trait={firstSelected} />}
      </SView>
    )
  }
}

function LearnMore({ trait }: { trait: TraitValue }) {
  const { getLanguageValue } = useContext(SessionContext)
  const [open, setOpen] = useState(false)
  const scale = useScale()
  const height = 20 * scale

  const { description, title } = trait
  if (!description || !title) return null

  return (
    <>
      <ChevronRightIcon height={height} width={height} />
      <SButton borderRadius={4} onClick={() => setOpen(true)}>
        Learn More
      </SButton>

      <SDialog
        w={800}
        p={50}
        open={open}
        onClose={() => setOpen(false)}
        title={getLanguageValue(title)}
      >
        <SMarkdown noScroll value={getLanguageValue(description)} />
      </SDialog>
    </>
  )
}
